import { Inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { fetch, optimisticUpdate, pessimisticUpdate } from '@ngrx/router-store/data-persistence';
import { Store } from '@ngrx/store';
import { undo } from 'ngrx-undo-v2';
import { from } from 'rxjs';
import { map, mapTo, switchMap } from 'rxjs/operators';
import { UnlockedCurriculumTreeQueries } from '.';
import {
  UNLOCKED_CURRICULUM_TREE_SERVICE_TOKEN,
  UnlockedCurriculumTreeServiceInterface,
} from '../../unlocked-curriculum-tree/unlocked-curriculum-tree.service.interface';
import { DalState } from '../dal.state.interface';
import { EffectFeedback, EffectFeedbackActions } from '../effect-feedback';
import { AddEffectFeedback } from '../effect-feedback/effect-feedback.actions';
import {
  DeleteUnlockedCurriculumTrees,
  LoadUnlockedCurriculumTrees,
  StartAddManyUnlockedCurriculumTrees,
  UnlockedCurriculumTreesActionTypes,
  UnlockedCurriculumTreesLoaded,
  UnlockedCurriculumTreesLoadError,
  UpsertUnlockedCurriculumTrees,
} from './unlocked-curriculum-tree.actions';

@Injectable()
export class UnlockedCurriculumTreeEffects {
  loadUnlockedCurriculumTrees$ = createEffect(() =>
    this.actions.pipe(
      ofType(UnlockedCurriculumTreesActionTypes.LoadUnlockedCurriculumTrees),
      concatLatestFrom(() => this.store.select(UnlockedCurriculumTreeQueries.getLoaded)),
      fetch({
        run: (action: LoadUnlockedCurriculumTrees, loaded: boolean) => {
          if (!action.payload.force && loaded) return;
          return this.unlockedCurriculumTreeService
            .getAllForUser(action.payload.userId)
            .pipe(map((unlockedCurriculumTrees) => new UnlockedCurriculumTreesLoaded({ unlockedCurriculumTrees })));
        },
        onError: (_action: LoadUnlockedCurriculumTrees, error) => {
          return new UnlockedCurriculumTreesLoadError(error);
        },
      })
    )
  );

  startAddManyUnlockedCurriculumTrees$ = createEffect(() =>
    this.actions.pipe(
      ofType(UnlockedCurriculumTreesActionTypes.StartAddManyUnlockedCurriculumTrees),
      pessimisticUpdate({
        run: (action: StartAddManyUnlockedCurriculumTrees) => {
          return this.unlockedCurriculumTreeService
            .createUnlockedCurriculumTrees(action.payload.userId, action.payload.unlockedCurriculumTrees)
            .pipe(
              switchMap((unlockedCurriculumTrees) => {
                return from([
                  new UpsertUnlockedCurriculumTrees({
                    unlockedCurriculumTrees: unlockedCurriculumTrees,
                  }),
                  new AddEffectFeedback({
                    effectFeedback: new EffectFeedback({
                      id: this.uuid(),
                      triggerAction: action,
                      message: 'Het thema is beschikbaar voor de leerlingen.',
                    }),
                  }),
                ]);
              })
            );
        },
        onError: (action: StartAddManyUnlockedCurriculumTrees, _error) => {
          const effectFeedback = EffectFeedback.generateErrorFeedback(
            this.uuid(),
            action,
            'Het is niet gelukt om het thema beschikbaar te maken.'
          );
          const effectFeedbackAction = new EffectFeedbackActions.AddEffectFeedback({ effectFeedback });
          return effectFeedbackAction;
        },
      })
    )
  );

  deleteUnlockedCurriculumTrees$ = createEffect(() =>
    this.actions.pipe(
      ofType(UnlockedCurriculumTreesActionTypes.DeleteUnlockedCurriculumTrees),
      optimisticUpdate({
        run: (action: DeleteUnlockedCurriculumTrees) => {
          return this.unlockedCurriculumTreeService
            .deleteUnlockedCurriculumTrees(action.payload.userId, action.payload.ids)
            .pipe(
              mapTo(
                new AddEffectFeedback({
                  effectFeedback: new EffectFeedback({
                    id: this.uuid(),
                    triggerAction: action,
                    message: 'Het thema is niet meer beschikbaar voor de leerlingen',
                  }),
                })
              )
            );
        },
        undoAction: (action: DeleteUnlockedCurriculumTrees, _error) => {
          const undoAction = undo(action);

          const effectFeedback = EffectFeedback.generateErrorFeedback(
            this.uuid(),
            action,
            'Het is niet gelukt om het thema te vergrendelen.'
          );
          const effectFeedbackAction = new EffectFeedbackActions.AddEffectFeedback({ effectFeedback });

          return from([undoAction, effectFeedbackAction]);
        },
      })
    )
  );

  constructor(
    @Inject('uuid')
    private uuid,
    private actions: Actions,
    private store: Store<DalState>,
    @Inject(UNLOCKED_CURRICULUM_TREE_SERVICE_TOKEN)
    private unlockedCurriculumTreeService: UnlockedCurriculumTreeServiceInterface
  ) {}
}
